import { queriesPayrollRuns } from "@query";
import { PayrollRun } from "@wingspanhq/wingspan-api-sdk";
import { useMemo } from "react";

import { PAYROLL_RUNS_ACTIVE_STATUSES } from "../../PayrollRuns/constants";

/**
 * Resolves the in-flight payroll run that an approved work log would join.
 *
 * `listPayrollRuns` exposes no `payPeriodId` filter (only type/status/cycleType/
 * payDate/externalId), so the period match has to happen client-side over the
 * account's active runs. `useQueryAll` makes that correct — the whole active set
 * is loaded, not a partial page. Same constraint the off-cycle modal documents.
 *
 * **No `type` filter.** This hook serves the approve modal, which handles both
 * contractor and employee work logs, so pinning it to one payroll type made it
 * miss every run of the other. `payPeriodId` is specific enough on its own — a
 * pay period belongs to one account and one run. (`filter[type]` is also not
 * required by the endpoint, contrary to the note in `PayrollRuns/constants.ts`;
 * verified against integration, which returns 200 without it.)
 */
export const useQueryPayPeriodPayrollRun = (
  payPeriodId: string | null | undefined
): { payrollRun: PayrollRun | undefined; isLoading: boolean } => {
  const query = queriesPayrollRuns.listPayrollRuns.useQueryAll(
    {
      filter: {
        status: { anyOf: PAYROLL_RUNS_ACTIVE_STATUSES }
      }
    },
    { enabled: !!payPeriodId }
  );

  const payrollRun = useMemo(
    () =>
      payPeriodId
        ? (query.flatData ?? []).find(run => run.payPeriodId === payPeriodId)
        : undefined,
    [query.flatData, payPeriodId]
  );

  return {
    payrollRun,
    // `isInitialLoading`, not `isLoading`: react-query v4 leaves a *disabled*
    // query in `status: "loading"` forever, so `isLoading` never goes false for
    // a work log with no pay period — and a contractor work log never has one.
    // Callers rendering a spinner on this would hang permanently.
    isLoading: query.isInitialLoading
  };
};
